import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Section, TableWrapper } from '../styles';
import { Table, Spin, Pagination, App, Tooltip } from 'antd';
import apiService from '../../../services/api';
import { ServiceType } from '../../../services/api';
import type {
    BalanceResponseDto,
} from '../../../api-schema/generated/types';
import styled from 'styled-components';
import {
    instrumentConfigApi,
    InstrumentConfigResponse,
    InstrumentConfigAggregates,
    ConfigType,
} from '../../../services/instrumentConfigApi';
import InstrumentConfigTable from '../components/InstrumentConfigTable';
import DepositWithdrawModal from '../../GridTrading/components/DepositWithdrawModal';
import { formatNumber, formatAbbrev } from '../../../utils/numberFormat';
import useIsMobile from '../../../hooks/useIsMobile';
import { HoldingRowData, IncomingQty, MobileHoldingsList, SharedBadge, reportsSettlement } from './HoldingsSettlement';

interface Portfolio {
    id: string;
    portfolioId: string;
    portfolioName: string;
    createdDate: string;
    status: 'active' | 'inactive' | 'closed';
}

export interface PortfolioTabProps {
    selectedPortfolio: Portfolio | undefined;
    executionId?: string | null;
    active?: boolean;
    refreshKey?: number;
    /** Re-fetch every N ms while the tab is active — set while the bot runs. Silent. */
    pollMs?: number;
    /**
     * Scope the InstrumentConfig list to a single strategy family so a portfolio
     * running both a grid and a pair shows only the rows that belong to the
     * current page. Required: the same component renders on /grid-trading and
     * /pair-trading and the two tabs must not bleed into each other.
     */
    configType: ConfigType;
    /** Pair only: the derivatives account picked on the page, used when no pair is placed yet. */
    derivPortfolioId?: string | null;
}

/**
 * GET /balance/available — the server's own allocation-headroom calculation.
 *
 * The panel used to recompute this client-side as `purchasingpower − totalDeposited +
 * positionCost`, which is wrong twice over: `purchasingpower` is already net of the cash
 * reserved behind resting BUY orders (money that is still part of an allocation's unspent
 * slice, so it gets debited a second time), and the aggregates are scoped to ONE
 * configType while the broker cash is shared by every strategy on the account. The server
 * measures gross cash against every non-CLOSED InstrumentConfig on the portfolio.
 */
interface AvailableCashResponse {
    portfolioId: string;
    purchasingPower: number;
    cash: number;
    reservedCash: number | null;
    committed: number;
    inHoldings: number;
    idle: number;
    available: number;
}

interface PortfolioHolding {
    symbol: string;
    quantity: number;
    availableqty: number;
    deliverableqty: number;
    stockdividend: number;
    capital: number;
    /** Buys still settling (T+2.5). Set only by brokers that report it (Pinetree). */
    t0buyqty?: number;
    t1buyqty?: number;
    t2buyqty?: number;
}


/**
 * GET /portfolios/:id/strategy-holdings: this strategy's own shares (engine-attributed, per
 * InstrumentConfig) with the broker's account-wide row beside them. Grid and pair share one
 * broker account, so `account` covers both whenever `sharedWith` is non-empty.
 */
interface StrategyHolding {
    exchange: string;
    symbol: string;
    quantity: number;
    /** Raw VND. */
    cost: number;
    averagePrice: number | null;
    sharedWith: ConfigType[];
    account: (Omit<PortfolioHolding, 'symbol' | 'capital'> & { capital: number | null }) | null;
}

interface PortfolioDetailResponse {
    id: string;
    portfolioId: string;
    brokerCode?: string;
    portfolioName?: string;
    portfolios: PortfolioHolding[];
    date: string;
    userId: string;
    status?: string;
    isActive?: boolean;
}

const coreService = apiService.getService(ServiceType.CORE);

const formatNum = (v: number) => formatNumber(v);

/**
 * Stat rows as equal grid columns rather than flex + a fixed min-width.
 *
 * Flex-wrapping sized every stat to its own content, so one long VND figure widened its
 * box, shoved the next stat onto a new line and left a hole beside it — the row "broke"
 * differently depending on how much money was in the account. Equal tracks that collapse
 * from four per row to two to one always land somewhere sane.
 */
const statRowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(min(150px, 100%), 1fr))',
    gap: 12,
    marginBottom: 12,
};

/** "123,456,789" — past this a stat is wider than a phone-width column at 18px. */
const EXACT_MAX_CHARS = 11;

interface BalanceStatProps {
    label: string;
    value: number;
    tip?: string;
    highlight?: boolean;
    negative?: boolean;
    /** True (default) renders as a VND figure with locale formatting. False renders as a plain integer (e.g. counts). */
    numeric?: boolean;
}

const BalanceStat: React.FC<BalanceStatProps> = ({ label, value, tip, highlight, negative, numeric = true }) => {
    const isMobile = useIsMobile();
    const labelEl = (
        <div
            style={{
                color: '#848E9C',
                fontSize: 11,
                marginBottom: 2,
                cursor: tip ? 'help' : undefined,
                borderBottom: tip ? '1px dotted #848E9C' : undefined,
                width: 'fit-content',
            }}
            title={tip}
        >
            {label}
        </div>
    );
    const color = negative ? '#F6465D' : highlight ? '#0ECB81' : '#EAECEF';
    const exact = numeric ? formatNum(Math.round(value)) : String(value);
    // A VND balance past a billion is 13 characters, wider than a column on a phone.
    // Abbreviate there and keep the exact figure one tap away in the title; desktop has
    // the room, so it stays exact — this is money, and a rounded number is not a number
    // anyone should have to reconcile against the broker.
    const abbreviate = numeric && isMobile && exact.length > EXACT_MAX_CHARS;
    return (
        <div style={{ minWidth: 0 }}>
            {labelEl}
            <div
                title={abbreviate ? exact : undefined}
                style={{
                    fontSize: isMobile ? 16 : 18,
                    fontWeight: 700,
                    color,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                {abbreviate ? formatAbbrev(value) : exact}
            </div>
        </div>
    );
};

// One leg of a pair's capital (Stock or Derivatives) as a self-contained,
// accent-bordered panel so the two legs read as distinct columns instead of a
// flat stack of identically-labelled stats.
const LegBalancePanel: React.FC<{
    title: string;
    accent: string;
    cash: number;
    available: number;
    cashTip?: string;
    availTip?: string;
}> = ({ title, accent, cash, available, cashTip, availTip }) => (
    <div style={{
        flex: 1,
        // min() so the panel collapses to the container on a phone instead of forcing
        // 220px and pushing its sibling off-screen.
        minWidth: 'min(220px, 100%)',
        background: '#1A1E24',
        border: '1px solid #2B3139',
        borderLeft: `3px solid ${accent}`,
        borderRadius: 6,
        padding: '12px 14px',
    }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }} />
            <span style={{ color: '#EAECEF', fontSize: 13, fontWeight: 700, letterSpacing: 0.3 }}>{title}</span>
        </div>
        <div style={{ ...statRowStyle, marginBottom: 0 }}>
            <BalanceStat label="My cash" tip={cashTip} value={cash} highlight />
            <BalanceStat label="Available allocation" tip={availTip} value={available} highlight />
        </div>
    </div>
);

const ScrollableWrap = styled.div`
    padding: 0 8px 8px 8px;
    height: 100%;
    overflow-y: auto;
    min-height: 0;
    width: 100%;
    box-sizing: border-box;
`;

const PortfolioTab: React.FC<PortfolioTabProps> = ({ selectedPortfolio, active, refreshKey, pollMs, configType, derivPortfolioId }) => {
    const { message, modal } = App.useApp();
    const isMobile = useIsMobile();
    const [strategyHoldings, setStrategyHoldings] = useState<StrategyHolding[] | null>(null);
    const [instrumentConfigs, setInstrumentConfigs] = useState<InstrumentConfigResponse[]>([]);
    const [icPage, setIcPage] = useState<number>(1);
    const [icPageSize, setIcPageSize] = useState<number>(12);
    const [icTotal, setIcTotal] = useState<number>(0);
    const [icAggregates, setIcAggregates] = useState<InstrumentConfigAggregates | null>(null);
    const [icLoading, setIcLoading] = useState<boolean>(false);
    const [balance, setBalance] = useState<BalanceResponseDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [modalMode, setModalMode] = useState<'DEPOSIT' | 'WITHDRAW' | null>(null);
    const [modalConfig, setModalConfig] = useState<InstrumentConfigResponse | null>(null);
    // Derivatives leg of a pair: its own broker balance + cash allocated to the
    // deriv leg, so Capital allocation can show the futures account next to stock.
    const [derivBalance, setDerivBalance] = useState<BalanceResponseDto | null>(null);
    const [derivAllocated, setDerivAllocated] = useState<number>(0);
    // Deriv-leg position cost (= live required margin from Pinetree), folded back
    // into Available allocation just like the stock leg adds back holdingsCost.
    const [derivRequiredMargin, setDerivRequiredMargin] = useState<number>(0);
    // Server-computed allocation headroom per leg. Null = the call failed; the render
    // falls back to the local approximation rather than showing nothing.
    const [availableCash, setAvailableCash] = useState<AvailableCashResponse | null>(null);
    const [derivAvailableCash, setDerivAvailableCash] = useState<AvailableCashResponse | null>(null);
    // The derivatives account itself: name + open futures positions, shown in its own card.
    const [derivDetail, setDerivDetail] = useState<PortfolioDetailResponse | null>(null);

    const fetchData = useCallback(async (silent = false) => {
        if (!selectedPortfolio?.id) {
            setStrategyHoldings(null);
            setBalance(null);
            setAvailableCash(null);
            setError(null);
            return;
        }
        if (!silent) {
            setLoading(true);
            setError(null);
        }
        const portfolioId = selectedPortfolio.id;
        try {
            const [holdingsRes, balanceRes, availableRes] = await Promise.all([
                coreService.get<StrategyHolding[]>(`/portfolios/${portfolioId}/strategy-holdings`, { params: { configType } }),
                coreService.get<BalanceResponseDto>('/balance', { params: { portfolioId } }).catch(() => null),
                coreService.get<AvailableCashResponse>('/balance/available', { params: { portfolioId } }).catch(() => null),
            ]);
            setStrategyHoldings(holdingsRes.data);
            setBalance(balanceRes?.data ?? null);
            setAvailableCash(availableRes?.data ?? null);
        } catch (err: any) {
            console.error('Error fetching portfolio data:', err);
            // A failed background poll keeps what is on screen.
            if (silent) return;
            setError(err.response?.data?.message || 'Failed to load portfolio data');
            setStrategyHoldings(null);
            setBalance(null);
            setAvailableCash(null);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [selectedPortfolio?.id, configType]);

    // Per-symbol capital list — paginated independently so the rest of the tab
    // doesn't re-fetch each time the user pages through symbols.
    const fetchInstrumentConfigs = useCallback(async (page: number, pageSize: number, silent = false) => {
        if (!selectedPortfolio?.id) {
            setInstrumentConfigs([]);
            setIcTotal(0);
            setIcAggregates(null);
            return;
        }
        try {
            if (!silent) setIcLoading(true);
            const res = await instrumentConfigApi.list({
                portfolio_id: selectedPortfolio.id,
                config_type: configType,
                status: 'ACTIVE',
                page,
                page_size: pageSize,
            });
            setInstrumentConfigs(res.items);
            setIcTotal(res.total);
            setIcAggregates(res.aggregates);
        } catch (err) {
            console.error('Error fetching instrument configs:', err);
            if (silent) return;
            setInstrumentConfigs([]);
            setIcTotal(0);
            setIcAggregates(null);
        } finally {
            if (!silent) setIcLoading(false);
        }
    }, [selectedPortfolio?.id, configType]);

    const totals = useMemo(() => ({
        // Committed, not contributed: realized PnL is folded into currentCapital as the bot
        // banks it. The headroom maths below wants the committed figure; the panel shows the
        // two halves separately so "deposited" is not claimed for money the bot earned.
        totalDeposited: Number(icAggregates?.totalDeposited ?? 0),
        netDeposits: Number(icAggregates?.totalNetDeposits ?? 0),
        // An older BE sends only the net; fall back to it so the panel never goes blank.
        depositedGross: Number(icAggregates?.totalDepositedGross ?? icAggregates?.totalNetDeposits ?? 0),
        withdrawnGross: icAggregates?.totalWithdrawnGross == null ? null : Number(icAggregates.totalWithdrawnGross),
        realizedCredited: Number(icAggregates?.totalRealizedCredited ?? 0),
        positionCost: Number(icAggregates?.positionCost ?? 0),
    }), [icAggregates]);

    /**
     * Allocation headroom = gross broker cash − the unspent slice of every allocation.
     * Prefer the server's number (`/balance/available`): it uses gross cash rather than
     * purchasing power, and it counts every strategy on the account rather than just this
     * page's configType. The local expression is only a fallback for when that call fails
     * — it is measured against PP, so it under-reports by the resting-order notional.
     */
    const availableFor = (
        server: AvailableCashResponse | null,
        fallbackPp: number,
        fallbackAllocated: number,
        fallbackInHoldings: number,
    ): number =>
        server ? server.available : fallbackPp - fallbackAllocated + fallbackInHoldings;

    const availTipFor = (server: AvailableCashResponse | null, legNoun: string): string =>
        server
            ? `Broker cash (${formatNum(Math.round(server.cash))}) minus the ${formatNum(Math.round(server.idle))} still unspent inside existing allocations. ` +
              (server.reservedCash
                  ? `${formatNum(Math.round(server.reservedCash))} of that cash is currently locked behind resting buy orders — it is counted once here, not twice. `
                  : '') +
              `Covers every strategy on this ${legNoun}, not just the tab you are on.`
            : `My cash minus the capital already allocated to this ${legNoun}, plus the position cost already held.`;

    // Pair only: resolve the deriv leg (active pair → allocation balance → its
    // portfolio) and pull that account's broker balance so the Capital
    // allocation card can render the futures account just like the stock one.
    // Soft-fails to "no deriv block" if the portfolio has no pair placed.
    const fetchDerivBalance = useCallback(async () => {
        const clear = () => {
            setDerivBalance(null);
            setDerivAllocated(0);
            setDerivRequiredMargin(0);
            setDerivAvailableCash(null);
            setDerivDetail(null);
        };
        if (configType !== 'PAIR' || !selectedPortfolio?.id) {
            clear();
            return;
        }
        try {
            const active = await coreService
                .get<{ dataConfigId: string | null }>(`/pair/active/${selectedPortfolio.id}`)
                .catch(() => null);
            const dataConfigId = active?.data?.dataConfigId;
            let derivPid: string | null | undefined = null;
            if (dataConfigId) {
                const alloc = await coreService.get<{ derivLeg?: { portfolioId?: string; allocatedCash?: string; requiredMargin?: string } }>(
                    `/pair/allocation/${dataConfigId}/balance`,
                );
                derivPid = alloc.data?.derivLeg?.portfolioId;
                setDerivAllocated(Number(alloc.data?.derivLeg?.allocatedCash ?? 0));
                setDerivRequiredMargin(Number(alloc.data?.derivLeg?.requiredMargin ?? 0));
            } else {
                setDerivAllocated(0);
                setDerivRequiredMargin(0);
            }
            // No placed pair yet: still show the account the page has picked.
            derivPid = derivPid || derivPortfolioId;
            if (!derivPid) {
                clear();
                return;
            }
            const [bal, avail, det] = await Promise.all([
                coreService
                    .get<BalanceResponseDto>('/balance', { params: { portfolioId: derivPid } })
                    .catch(() => null),
                coreService
                    .get<AvailableCashResponse>('/balance/available', { params: { portfolioId: derivPid } })
                    .catch(() => null),
                coreService
                    .get<PortfolioDetailResponse>(`/portfolios/${derivPid}`)
                    .catch(() => null),
            ]);
            setDerivBalance(bal?.data ?? null);
            setDerivAvailableCash(avail?.data ?? null);
            setDerivDetail(det?.data ?? null);
        } catch (err) {
            console.error('Error fetching deriv leg balance:', err);
            clear();
        }
    }, [configType, selectedPortfolio?.id, derivPortfolioId]);

    // Resolve the pair DataConfig.id for this portfolio (null if no pair placed) and
    // pull pair-level capacity so the Total cash row can render with real numbers and
    // the buttons know whether the bot is running. Soft-fail on either call: the
    // panel just hides if we can't get a dataConfigId.
    // Headroom-only refresh after a cash op. Deliberately not `fetchData`, which flips the
    // whole tab into its loading spinner for a number that changed by one deposit.
    const refreshAvailableCash = useCallback(async () => {
        const portfolioId = selectedPortfolio?.id;
        if (!portfolioId) return;
        const res = await coreService
            .get<AvailableCashResponse>('/balance/available', { params: { portfolioId } })
            .catch(() => null);
        if (res) setAvailableCash(res.data);
    }, [selectedPortfolio?.id]);

    const handleSuccess = (updated: InstrumentConfigResponse) => {
        setInstrumentConfigs((prev) => prev.map((it) => (it.id === updated.id ? updated : it)));
        setModalMode(null);
        setModalConfig(null);
        // Re-fetch the current page so aggregates (totalDeposited / positionCost) refresh.
        void fetchInstrumentConfigs(icPage, icPageSize);
        // A cash op can change the deriv leg's allocation too — refresh it.
        void fetchDerivBalance();
        void refreshAvailableCash();
    };

    // Delete (close) a fully-drained leg allocation. The BE refuses (409) unless
    // currentCapital=0, no open position, and no PENDING cash-op — the card only enables
    // the button when drained, so this is the confirm + call + refresh wiring.
    const handleDeleteAllocation = (cfg: InstrumentConfigResponse) => {
        modal.confirm({
            title: `Delete allocation ${cfg.exchange}:${cfg.symbol}?`,
            content:
                'Closes the fully-drained allocation and removes the symbol from the config. ' +
                'Deposit/withdraw history is kept. You can re-add the symbol later.',
            okText: 'Delete',
            okButtonProps: { danger: true },
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await instrumentConfigApi.remove(cfg.id);
                    message.success(`Deleted allocation ${cfg.symbol}`);
                    void fetchInstrumentConfigs(icPage, icPageSize);
                    void fetchDerivBalance();
                    void refreshAvailableCash();
                } catch (e: unknown) {
                    const msg = e instanceof Error ? e.message : String(e);
                    message.error(`Delete failed: ${msg}`);
                }
            },
        });
    };

    useEffect(() => {
        if (active) fetchData();
    }, [active, refreshKey, fetchData]);

    useEffect(() => {
        if (active) void fetchDerivBalance();
    }, [active, refreshKey, fetchDerivBalance]);

    // Reset to page 1 whenever portfolio changes; the page-fetch effect below picks it up.
    useEffect(() => {
        setIcPage(1);
    }, [selectedPortfolio?.id]);

    useEffect(() => {
        if (active) {
            void fetchInstrumentConfigs(icPage, icPageSize);
        }
    }, [active, refreshKey, icPage, icPageSize, fetchInstrumentConfigs]);

    // While the bot runs, balances, PnL and per-symbol capital move on every fill.
    useEffect(() => {
        if (!active || !pollMs) return;
        const id = window.setInterval(() => {
            void fetchData(true);
            void fetchDerivBalance();
            void fetchInstrumentConfigs(icPage, icPageSize, true);
        }, pollMs);
        return () => window.clearInterval(id);
    }, [active, pollMs, fetchData, fetchDerivBalance, fetchInstrumentConfigs, icPage, icPageSize]);

    if (!selectedPortfolio?.id) {
        return (
            <ScrollableWrap>
                <Section>
                    <Card>
                        <div style={{ textAlign: 'center', padding: 32, color: '#848E9C' }}>
                            Please select a portfolio to view details.
                        </div>
                    </Card>
                </Section>
            </ScrollableWrap>
        );
    }

    if (loading) {
        return (
            <ScrollableWrap>
                <Section>
                    <Card>
                        <div style={{ textAlign: 'center', padding: 48 }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16, color: '#848E9C' }}>Loading...</div>
                        </div>
                    </Card>
                </Section>
            </ScrollableWrap>
        );
    }

    if (error) {
        return (
            <ScrollableWrap>
                <Section>
                    <Card>
                        <div style={{ textAlign: 'center', padding: 32, color: '#F6465D' }}>{error}</div>
                    </Card>
                </Section>
            </ScrollableWrap>
        );
    }

    if (!strategyHoldings) {
        return (
            <ScrollableWrap>
                <Section>
                    <Card>
                        <div style={{ textAlign: 'center', padding: 32, color: '#848E9C' }}>
                            No portfolio data available.
                        </div>
                    </Card>
                </Section>
            </ScrollableWrap>
        );
    }

    // Quantity and Capital are this strategy's own; Available, Incoming, Deliverable and
    // Stock Dividend are the broker's, i.e. the whole account's on a shared symbol.
    const holdings: HoldingRowData[] = strategyHoldings.map((h) => ({
        symbol: h.symbol,
        quantity: h.quantity,
        capital: h.cost,
        availableqty: h.account?.availableqty ?? 0,
        deliverableqty: h.account?.deliverableqty ?? 0,
        stockdividend: h.account?.stockdividend ?? 0,
        t0buyqty: h.account?.t0buyqty,
        t1buyqty: h.account?.t1buyqty,
        t2buyqty: h.account?.t2buyqty,
        sharedWith: h.sharedWith,
        accountQuantity: h.account?.quantity ?? null,
    }));
    const anyShared = holdings.some((h) => h.sharedWith.length > 0);
    const accountTitle = (label: string) =>
        anyShared ? <Tooltip title="Broker figure for the whole account: on a shared symbol it includes the other strategy's shares.">{label}</Tooltip> : label;

    return (
        <ScrollableWrap>
            {/* Capital + balance overview */}
            {(balance || icTotal > 0) && (
                <Section style={{ marginBottom: 8, width: '100%' }}>
                    <Card title="Capital allocation" style={{ width: '100%' }}>
                        {/* Broker-side balance. A pair shows its two legs as
                            side-by-side accent panels (Stock vs Derivatives) so the
                            identically-labelled stats don't blur together; a grid
                            keeps the single flat layout. */}
                        {configType === 'PAIR' ? (
                            (balance || derivBalance) && (
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'stretch' }}>
                                    {balance && (
                                        <LegBalancePanel
                                            title="Stock leg"
                                            accent="#0ECB81"
                                            cash={Number(balance.purchasingpower)}
                                            available={availableFor(availableCash, Number(balance.purchasingpower), totals.totalDeposited, totals.positionCost)}
                                            cashTip="Purchasing power — broker cash minus whatever is reserved behind resting buy orders."
                                            availTip={availTipFor(availableCash, 'account')}
                                        />
                                    )}
                                    {derivBalance && (
                                        <LegBalancePanel
                                            title="Derivatives leg"
                                            accent="#F6465D"
                                            cash={Number(derivBalance.purchasingpower)}
                                            available={availableFor(derivAvailableCash, Number(derivBalance.purchasingpower), derivAllocated, derivRequiredMargin)}
                                            cashTip="Purchasing power — broker cash / collateral available on the derivatives account."
                                            availTip={availTipFor(derivAvailableCash, 'derivatives account')}
                                        />
                                    )}
                                </div>
                            )
                        ) : (
                            balance && (
                                <>
                                    <div style={{ color: '#848E9C', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                                        Account balance
                                    </div>
                                    <div style={statRowStyle}>
                                        <BalanceStat
                                            label="My cash"
                                            tip="Purchasing power — broker cash minus whatever is reserved behind resting buy orders."
                                            value={balance.purchasingpower}
                                            highlight
                                        />
                                        <BalanceStat
                                            label="Available allocation"
                                            tip={availTipFor(availableCash, 'account')}
                                            value={availableFor(
                                                availableCash,
                                                Number(balance.purchasingpower),
                                                totals.totalDeposited,
                                                totals.positionCost,
                                            )}
                                            highlight
                                        />
                                    </div>
                                </>
                            )
                        )}

                        {/* Row 2: per-symbol capital totals + paginated table. Always
                            rendered so an empty allocation list shows a clear empty-state
                            instead of the whole section disappearing. */}
                        <>
                                <div style={{ color: '#848E9C', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                                    Capital deployed to symbols
                                </div>
                                {icTotal > 0 && (
                                    <div style={statRowStyle}>
                                        <BalanceStat label="Symbols" value={icTotal} numeric={false} />
                                        <BalanceStat
                                            label="Deposited"
                                            tip="Cash you put into these symbols. Cash a pair moved between its own legs is not counted."
                                            value={totals.depositedGross}
                                        />
                                        {totals.withdrawnGross !== null && (
                                            <BalanceStat
                                                label="Withdrawn"
                                                tip="Cash taken out of these symbols back to your account, including when a pair ended."
                                                value={totals.withdrawnGross}
                                            />
                                        )}
                                        <BalanceStat
                                            label="Realized PnL"
                                            tip="Realized PnL already added to the capital above. Not the bots' lifetime realized PnL."
                                            value={totals.realizedCredited}
                                        />
                                        <BalanceStat
                                            label="Position cost"
                                            tip={configType === 'PAIR'
                                                ? 'Cost basis of the stock holdings plus margin posted for open futures contracts.'
                                                : 'Cost basis of holdings the bot is currently carrying.'}
                                            value={totals.positionCost}
                                        />
                                    </div>
                                )}
                                <Spin spinning={icLoading}>
                                    {!icLoading && icTotal === 0 ? (
                                        <div
                                            style={{
                                                color: '#848E9C',
                                                textAlign: 'center',
                                                padding: '28px 16px',
                                                border: '1px dashed #2B3139',
                                                borderRadius: 6,
                                                fontSize: 13,
                                            }}
                                        >
                                            No allocations yet. Deposit to fund a symbol.
                                        </div>
                                    ) : (
                                        <InstrumentConfigTable
                                            configs={instrumentConfigs}
                                            onDeposit={(cfg) => { setModalConfig(cfg); setModalMode('DEPOSIT'); }}
                                            onWithdraw={(cfg) => { setModalConfig(cfg); setModalMode('WITHDRAW'); }}
                                            onDelete={handleDeleteAllocation}
                                        />
                                    )}
                                </Spin>
                                {icTotal > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                        <Pagination
                                            current={icPage}
                                            pageSize={icPageSize}
                                            total={icTotal}
                                            showSizeChanger
                                            pageSizeOptions={[12, 24, 48, 96]}
                                            showTotal={(t, [from, to]) => `${from}–${to} of ${t} symbols`}
                                            onChange={(p, size) => {
                                                if (size !== icPageSize) {
                                                    setIcPageSize(size);
                                                    setIcPage(1);
                                                } else {
                                                    setIcPage(p);
                                                }
                                            }}
                                            size="small"
                                        />
                                    </div>
                                )}
                            </>
                    </Card>
                </Section>
            )}

            <DepositWithdrawModal
                open={modalMode !== null && modalConfig !== null}
                mode={modalMode ?? 'DEPOSIT'}
                config={modalConfig}
                portfolioId={selectedPortfolio?.id ?? null}
                onCancel={() => { setModalMode(null); setModalConfig(null); }}
                onSuccess={handleSuccess}
            />

            {/* Holdings (old content) */}
            <Section style={{ marginBottom: 8, width: '100%' }}>
                <Card title={`${configType === 'PAIR' ? 'Stock holdings' : 'Holdings'} (${holdings.length})`} style={{ width: '100%' }}>
                    {holdings.length === 0 ? (
                        <div style={{ color: '#848E9C', textAlign: 'center', padding: 16 }}>No holdings found.</div>
                    ) : isMobile ? (
                        <MobileHoldingsList holdings={holdings} />
                    ) : (
                        <TableWrapper>
                            <Table
                                dataSource={holdings.map((h, i) => ({ key: `${h.symbol}-${i}`, ...h }))}
                                pagination={holdings.length > 20 ? { defaultPageSize: 20, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] } : false}
                                size="small"
                                // The columns add up to ~570px and Symbol is `fixed: left`,
                                // which antd only honours once a horizontal scroll exists.
                                scroll={{ x: 'max-content' }}
                                columns={[
                                    {
                                        title: 'Symbol', dataIndex: 'symbol', key: 'symbol', width: 140, fixed: 'left' as const,
                                        render: (s: string, h: HoldingRowData) => (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <span style={{ fontWeight: 600, color: '#EAECEF' }}>{s}</span>
                                                <SharedBadge holding={h} />
                                            </span>
                                        ),
                                    },
                                    { title: 'Quantity', dataIndex: 'quantity', key: 'quantity', width: 110, align: 'right' as const, render: (qty: number) => <span style={{ color: qty >= 0 ? '#0ECB81' : '#F6465D', fontWeight: 600 }}>{formatNum(qty)}</span> },
                                    { title: accountTitle('Available'), dataIndex: 'availableqty', key: 'availableqty', width: 110, align: 'right' as const, render: (q: number) => formatNum(q ?? 0) },
                                    ...(reportsSettlement(holdings)
                                        ? [{
                                            title: <Tooltip title={`Shares bought but not yet settled (T+2.5)${anyShared ? '. Whole account on a shared symbol.' : ''}`}>Incoming</Tooltip>,
                                            key: 'incoming',
                                            width: 170,
                                            align: 'right' as const,
                                            render: (_: unknown, h: HoldingRowData) => <IncomingQty holding={h} />,
                                        }]
                                        : []),
                                    { title: accountTitle('Deliverable'), dataIndex: 'deliverableqty', key: 'deliverableqty', width: 110, align: 'right' as const, render: (q: number) => formatNum(q ?? 0) },
                                    { title: accountTitle('Stock Dividend'), dataIndex: 'stockdividend', key: 'stockdividend', width: 120, align: 'right' as const, render: (q: number) => formatNum(q ?? 0) },
                                    { title: 'Capital', dataIndex: 'capital', key: 'capital', width: 150, align: 'right' as const, render: (c: number) => <span style={{ fontWeight: 600, color: '#EAECEF' }}>{formatNum(Math.round(c ?? 0))}</span> },
                                ]}
                            />
                        </TableWrapper>
                    )}
                </Card>
            </Section>

            {configType === 'PAIR' && (derivBalance || derivDetail) && (
                <Section style={{ marginBottom: 8, width: '100%' }}>
                    <Card
                        title={`Derivatives portfolio${derivDetail?.portfolioName ? ` · ${derivDetail.portfolioName}` : ''}`}
                        style={{ width: '100%' }}
                    >
                        {/* The account's balance figures live on the Portfolio page. */}
                        {(() => {
                            const positions = (derivDetail?.portfolios ?? []).filter((h) => Number(h.quantity) !== 0);
                            if (positions.length === 0) {
                                return <div style={{ color: '#848E9C', textAlign: 'center', padding: 16 }}>No open futures positions.</div>;
                            }
                            return isMobile ? (
                                <MobileHoldingsList holdings={positions} />
                            ) : (
                                <TableWrapper>
                                    <Table
                                        dataSource={positions.map((h, i) => ({ key: `${h.symbol}-${i}`, ...h }))}
                                        pagination={false}
                                        size="small"
                                        columns={[
                                            { title: 'Contract', dataIndex: 'symbol', key: 'symbol', render: (v: string) => <span style={{ fontWeight: 600, color: '#EAECEF' }}>{v}</span> },
                                            {
                                                title: <Tooltip title="Positive = long, negative = short">Contracts</Tooltip>,
                                                dataIndex: 'quantity', key: 'quantity', align: 'right' as const,
                                                render: (q: number) => <span style={{ color: q >= 0 ? '#0ECB81' : '#F6465D', fontWeight: 600 }}>{formatNum(q)}</span>,
                                            },
                                            { title: 'Available', dataIndex: 'availableqty', key: 'availableqty', align: 'right' as const, render: (q: number) => formatNum(q ?? 0) },
                                        ]}
                                    />
                                </TableWrapper>
                            );
                        })()}
                    </Card>
                </Section>
            )}
        </ScrollableWrap>
    );
};

export default PortfolioTab;
